<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="() => {}" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <SpinnerLoader v-if="isSubmitting" />

              <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ userToEdit ? $t('forms.edit') : $t('forms.createUser') }}
                </h3>
                <button
                  @click="closeModal"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>
              <div class="p-6 overflow-y-auto">
                <form @submit.prevent="submitForm" id="user-form" class="flex flex-col gap-4">
                  <fieldset
                    :disabled="adminStore.isSubmitting"
                    class="contents :disabled:cursor-default"
                  >
                    <BaseInput
                      id="firstName"
                      v-model="form.userPrenom.value.value"
                      :label="$t('auth.becomeMember.form.firstName') + ' *'"
                      :placeholder="$t('auth.becomeMember.form.firstName')"
                      :error="form.userPrenom.errorMessage.value"
                      @blur="form.userPrenom.handleChange"
                      width="w-full"
                    />

                    <BaseInput
                      id="lastName"
                      v-model="form.userNom.value.value"
                      :label="$t('auth.becomeMember.form.lastName') + ' *'"
                      :placeholder="$t('auth.becomeMember.form.lastName')"
                      :error="form.userNom.errorMessage.value"
                      @blur="form.userNom.handleChange"
                      width="w-full"
                    />

                    <BaseInput
                      id="email"
                      type="email"
                      v-model="form.userEmail.value.value"
                      :label="$t('auth.becomeMember.form.email') + ' *'"
                      :placeholder="$t('auth.becomeMember.form.email')"
                      :error="form.userEmail.errorMessage.value"
                      @blur="form.userEmail.handleChange"
                      width="w-full"
                    />

                    <!-- <BaseInput
                    id="organisation"
                    v-model="form.organisation.value.value"
                    :label="$t('auth.becomeMemberAskRoles.form.organization')"
                    :placeholder="$t('auth.becomeMemberAskRoles.form.organization')"
                    :error="form.organisation.errorMessage.value"
                    @blur="form.organisation.handleChange"
                    width="w-full"
                  /> -->

                    <!-- <BaseInput
                    id="position"
                    v-model="form.position.value.value"
                    :label="$t('auth.becomeMemberAskRoles.form.functions')"
                    :placeholder="$t('auth.becomeMemberAskRoles.form.functions')"
                    :error="form.position.errorMessage.value"
                    @blur="form.position.handleChange"
                    width="w-full"
                  /> -->

                    <!-- <BaseInput
                    id="phone"
                    v-model="form.phone.value.value"
                    :label="$t('auth.becomeMemberAskRoles.form.telephone')"
                    :placeholder="$t('auth.becomeMemberAskRoles.form.telephone')"
                    :error="form.phone.errorMessage.value"
                    @blur="form.phone.handleChange"
                    wi dth="w-full" />-->

                    <div class="mt-2 bg-blue-50 border border-blue-200 rounded-md p-4">
                      <span class="block text-sm font-medium text-gray-800 mb-1">
                        {{ $t('auth.editForm.requestedRoles') }}
                      </span>
                      <p class="text-xs text-gray-500 mb-3">
                        {{
                          profilVerrouille
                            ? $t('admin.users.ownProfileLocked')
                            : $t('admin.users.oneProfileHint')
                        }}
                      </p>

                      <div
                        v-for="groupe in groupesDeProfils"
                        :key="groupe.portee"
                        class="mb-3 last:mb-0"
                      >
                        <span
                          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2"
                        >
                          {{ groupe.titre }}
                        </span>
                        <div class="flex flex-col gap-3">
                          <label
                            v-for="profil in groupe.profils"
                            :key="profil.profilRefCode"
                            :for="`profil-${profil.profilRefCode}`"
                            class="flex items-center gap-4 text-sm select-none"
                            :class="
                              profilSelectionnable(profil)
                                ? 'cursor-pointer text-gray-700'
                                : 'cursor-not-allowed text-gray-400'
                            "
                          >
                            <input
                              :id="`profil-${profil.profilRefCode}`"
                              v-model="profilCode"
                              type="radio"
                              name="profil"
                              :value="profil.profilRefCode"
                              :disabled="!profilSelectionnable(profil)"
                              class="sr-only"
                            />
                            <span
                              class="relative h-5 w-5 shrink-0 rounded-full border bg-white"
                              :class="
                                profilCode === profil.profilRefCode
                                  ? 'border-main'
                                  : 'border-gray-400'
                              "
                            >
                              <span
                                v-if="profilCode === profil.profilRefCode"
                                class="absolute inset-1 rounded-full bg-main"
                              />
                            </span>
                            <span>{{ libelleProfil(profil) }}</span>
                            <span
                              v-if="profilDemande(profil)"
                              class="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
                            >
                              {{ $t('auth.editForm.waitingValidation') }}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <!-- Rattachement communal : seulement pour un profil communal -->
                    <div v-if="profilEstCommunal" class="flex flex-col gap-4 pt-1">
                      <Autocomplete
                        v-model="commune"
                        :items="communes"
                        :item-title="(c: CommuneApiResponse) => libelleCommune(c)"
                        :item-value="(c: CommuneApiResponse) => c"
                        :label="$t('admin.users.commune') + ' *'"
                        :placeholder="
                          chargementCommunes
                            ? $t('admin.users.communesLoading')
                            : $t('admin.users.communePlaceholder')
                        "
                        :disabled="profilVerrouille || chargementCommunes"
                        :error-messages="communeManquante ? $t('admin.users.communeRequired') : undefined"
                        clearable
                      />
                    </div>
                  </fieldset>
                </form>
              </div>

              <div class="px-6 py-4 flex justify-between items-center border-t border-gray-200">
                <button
                  type="button"
                  class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline transition-colors"
                  @click="closeModal"
                >
                  {{ $t('forms.cancel') }}
                </button>

                <button
                  type="submit"
                  form="user-form"
                  :disabled="isSubmitting || !meta.valid || communeManquante || !profilSelectionne"
                  class="inline-flex justify-center rounded-md bg-main hover:bg-main-hover px-6 py-2.5 text-sm font-medium text-white focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors"
                >
                  <Icon v-if="isSubmitting" icon="eos-icons:loading" class="mr-2 h-4 w-4" />
                  {{ submitLabel }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import Autocomplete from '@/components/ui/Autocomplete.vue'

import { onInvalidSubmit } from '@/services/forms/FormService'
import { UserProfileForm } from '@/services/userAndAuth/forms/UserProfileForm'
import { ProfilService } from '@/services/userAndAuth/ProfilService'
import { CommuneService, type CommuneApiResponse } from '@/services/commune/CommuneService'
import { useAdminStore } from '@/stores/adminStore'
import { useUserStore } from '@/stores/userStore'
import type { User, UserAdminSubmission } from '@/models/interfaces/auth/User'
import { ProfilCode, ProfilPortee, type Profil } from '@/models/interfaces/auth/Profil'
import { useI18n } from 'vue-i18n'
import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'

/**
 * Habilitations qui donnent prise sur les comptes : un profil qui en porte une que l'on n'a pas
 * soi-même ne peut être attribué (même règle que App\Security\ProfilPrivileges côté backend).
 */
const HABILITATIONS_SENSIBLES = [
  'SUPER_ADMIN',
  'GESTION_HABILITATIONS',
  'READ_UTILISATEUR',
  'CREATE_UTILISATEUR',
  'WRITE_UTILISATEUR',
  'DELETE_UTILISATEUR',
  'WRITE_HABILITATION',
  'DELETE_HABILITATION',
]

const { t, te } = useI18n()
const adminStore = useAdminStore()
const userStore = useUserStore()

const isOpen = computed(() => adminStore.userEdition.active)

const closeModal = () => {
  adminStore.userEdition.active = false
}

const userToEdit: User | null = adminStore.userEdition.user
const { form, handleSubmit, meta } = UserProfileForm.getUserEditionForm(userToEdit)
const isSubmitting = computed(() => adminStore.isSubmitting)

const submitLabel = computed(() => {
  if (userToEdit) {
    return !userToEdit.isValidated ? t('forms.validate') : t('forms.edit')
  } else {
    return t('forms.create')
  }
})

// Profil unique de l'utilisateur. Un nouveau compte part du profil par défaut, celui d'une
// inscription depuis l'accueil.
const profils = ref<Profil[]>([])
const profilCode = ref<string>(userToEdit?.profil?.profilRefCode ?? ProfilCode.LECTEUR_CENTRAL)
const profilSelectionne = computed(() =>
  profils.value.find((p) => p.profilRefCode === profilCode.value),
)
const profilEstCommunal = computed(
  () => profilSelectionne.value?.profilPortee === ProfilPortee.COMMUNAL,
)

/** Personne ne modifie son propre profil : le backend le refuse aussi. */
const profilVerrouille = computed(
  () => !!userToEdit && userToEdit.id === userStore.currentUser?.id,
)

const groupesDeProfils = computed(() =>
  [
    { portee: ProfilPortee.CENTRAL, titre: t('admin.users.centralProfiles') },
    { portee: ProfilPortee.COMMUNAL, titre: t('admin.users.communalProfiles') },
  ].map((groupe) => ({
    ...groupe,
    profils: profils.value.filter((p) => p.profilPortee === groupe.portee),
  })),
)

const profilSelectionnable = (profil: Profil) =>
  !profilVerrouille.value &&
  (userStore.userIsSuperAdmin() ||
    (profil.habilitations ?? [])
      .filter((code) => HABILITATIONS_SENSIBLES.includes(code))
      .every((code) => userStore.currentUser?.roles.includes(`ROLE_${code}` as never)))

const libelleProfil = (profil: Profil) => {
  const cle = `auth.profils.${profil.profilRefCode}`
  return te(cle) ? t(cle) : profil.profilTitre
}

/** Profil demandé par l'utilisateur depuis son compte, en attente d'approbation. */
const profilDemande = (profil: Profil) =>
  profil.profilRefCode !== userToEdit?.profil?.profilRefCode &&
  !!userToEdit?.userRequestedRoles?.includes(`ROLE_${profil.profilRefCode}` as never)

// Commune de rattachement, chargée à la première sélection d'un profil communal.
const communes = ref<CommuneApiResponse[]>([])
const commune = ref<CommuneApiResponse | null>(null)
const chargementCommunes = ref(false)
const communeManquante = computed(() => profilEstCommunal.value && !commune.value)

const libelleCommune = (c: CommuneApiResponse | null) =>
  c ? [c.cmnTitre, c.cmnDept].filter(Boolean).join(' — ') : ''

const chargerCommunes = async () => {
  if (communes.value.length > 0 || chargementCommunes.value) return
  chargementCommunes.value = true
  try {
    communes.value = (await CommuneService.getAllCommunes()).sort((a, b) =>
      String(a.cmnTitre ?? '').localeCompare(String(b.cmnTitre ?? ''), 'fr'),
    )
    const cmnCode = userToEdit?.commune?.cmnCode
    if (cmnCode && !commune.value) {
      commune.value = communes.value.find((c) => c.cmnCode === cmnCode) ?? null
    }
  } finally {
    chargementCommunes.value = false
  }
}

watch(profilEstCommunal, (communal) => {
  if (communal) chargerCommunes()
})

onMounted(async () => {
  profils.value = await ProfilService.getProfils()
  if (profilEstCommunal.value) await chargerCommunes()
})

const submitForm = handleSubmit(
  async (values) => {
    const userSubmission: UserAdminSubmission = {
      ...values,
      userRequestedRoles: [],
      isValidated: true,
    }
    if (!profilVerrouille.value && profilSelectionne.value) {
      userSubmission.profil = profilSelectionne.value['@id']
      userSubmission.commune = profilEstCommunal.value ? (commune.value?.['@id'] ?? null) : null
    }
    if (userToEdit) {
      await adminStore.editUser(userSubmission)
    } else {
      await adminStore.createUser(userSubmission)
    }
    if (!adminStore.isError) {
      closeModal()
    }
  },
  () => onInvalidSubmit,
)
</script>
